import { useState } from "react";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { Card, Divider, IconButton, Stack, Typography } from "@mui/material";

import { useDarkThemeCheck } from "../../../../hooks/useDarkThemeCheck";
import { type AssetDescriptor, type BillingProfileInvoiceBucket } from "../../../BillingProfileForm.models";
import { AssetLogo } from "./AssetsLogos";
import { AssetsMultiselect } from "./AssetsMultiselect";
import { getBucketTypeDisplayName } from "./utils";

const getStarIconColor = (isDarkTheme: boolean) => (isDarkTheme ? "#FF9800" : "#FFb74D");

const BucketCard = ({
  bucket,
  openBucketEditForm,
  unassignedAssets,
  assignAssetsToBucket,
  openDeleteBucketDialog,
  setBucketAsDefault,
}: {
  bucket: BillingProfileInvoiceBucket;
  openBucketEditForm: (bucket: BillingProfileInvoiceBucket) => void;
  unassignedAssets: AssetDescriptor[];
  assignAssetsToBucket: (bucket: BillingProfileInvoiceBucket, assets: AssetDescriptor[]) => void;
  openDeleteBucketDialog: (bucket: BillingProfileInvoiceBucket) => void;
  setBucketAsDefault: (bucket: BillingProfileInvoiceBucket) => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { name, isDefault, assets } = bucket;
  const bucketType = bucket.type;
  const bucketTypeName = getBucketTypeDisplayName(bucketType);
  const isDarkTheme = useDarkThemeCheck();
  const starIconColor = getStarIconColor(isDarkTheme);

  return (
    <Card>
      <Stack sx={{ p: 3 }} spacing={3}>
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center" }}
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => {
            setIsHovered(false);
          }}
        >
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <AssetLogo assetType={bucketType} />
            <Divider orientation="vertical" flexItem />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, whiteSpace: "nowrap" }}>
              {name}
            </Typography>
            <Divider orientation="vertical" flexItem />

            {isDefault && (
              <Stack direction="row" spacing={1} alignItems="center">
                <Stack>
                  <StarIcon sx={{ color: starIconColor }} fontSize="medium" />
                </Stack>
                <Typography variant="caption">Default bucket for {bucketTypeName}</Typography>
              </Stack>
            )}

            {!isDefault && (
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  size="small"
                  color="inherit"
                  sx={{ padding: "0px" }}
                  onClick={() => {
                    setBucketAsDefault(bucket);
                  }}
                >
                  <StarOutlineIcon />
                </IconButton>
                {<Typography variant="caption">Set as default bucket for {bucketTypeName}</Typography>}
              </Stack>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ opacity: isHovered ? 1 : 0 }}>
            <IconButton
              aria-label="edit"
              size="small"
              onClick={() => {
                openBucketEditForm(bucket);
              }}
            >
              <EditIcon />
            </IconButton>

            <IconButton
              size="small"
              aria-label="delete"
              onClick={() => {
                openDeleteBucketDialog(bucket);
              }}
            >
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        </Stack>

        <AssetsMultiselect
          value={assets}
          bucketType={bucketType}
          availableAssets={unassignedAssets}
          onChange={(assets) => {
            assignAssetsToBucket(bucket, assets);
          }}
        />
      </Stack>
    </Card>
  );
};

export default BucketCard;
